/* src/modules/performance/PerformanceModule.js */

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { TrendingUpIcon } from '../../components/icons/Icons';

// Import Icons for the header
import { CheckBadgeIcon } from '../../components/icons/Icons';

// --- Import Tab Components ---
// NOTE: Create these files next. For now, the app will look for them.
import MyPerformance from './MyPerformance';
import TeamPerformance from './TeamPerformance';
import PerformanceSetup from './PerformanceSetup';

export default function PerformanceModule({ userProfile, showModal, appId }) {
    const [activeTab, setActiveTab] = useState('myPerformance');
    const [isActingManager, setIsActingManager] = useState(false);
    const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

    // 1. Check Permissions: Is the user a manager? 
    // (Similar logic to your ExpenseStatements.js)
    useEffect(() => {
        const checkManagerStatus = async () => {
            // Admins and Approvers are automatically managers
            if (userProfile.role === 'Admin' || userProfile.role === 'Approver') {
                setIsActingManager(true);
                setIsLoadingPermissions(false);
                return;
            }

            // Check if anyone reports to this user in the users collection
            try {
                const usersRef = collection(db, `/artifacts/${appId}/users`);
                const q = query(usersRef, where("managerId", "==", userProfile.uid), limit(1));
                const snapshot = await getDocs(q);
                setIsActingManager(!snapshot.empty);
            } catch (error) {
                console.error("Error checking manager status:", error);
                setIsActingManager(false);
            } finally {
                setIsLoadingPermissions(false);
            }
        };

        checkManagerStatus();
    }, [appId, userProfile]);

    // 2. Define Tab Visibility Logic
    const canViewTeam = useMemo(() => {
        return userProfile.role === 'Admin' || isActingManager;
    }, [userProfile, isActingManager]);

    const canViewSetup = useMemo(() => {
        return userProfile.role === 'Admin' || userProfile.department === 'HR';
    }, [userProfile]);

    if (isLoadingPermissions) return <LoadingSpinner />;

    return (
        <div className="bg-white p-8 rounded-lg shadow-md min-h-[600px]">
            {/* Header Section */}
            <div className="flex items-center space-x-3 mb-6">
                <TrendingUpIcon className="h-8 w-8 text-[#20BEC6]" />
                <div>
                    <h2 className="text-2xl font-semibold text-[#213368]">Performance Management</h2>
                    <p className="text-sm text-gray-500">Track goals, complete reviews, and manage team progress.</p>
                </div>
            </div>

            {/* Tab Navigation (Styled like Expense Module) */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('myPerformance')}
                        className={`${
                            activeTab === 'myPerformance' 
                                ? 'border-[#20BEC6] text-[#20BEC6]' 
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                    >
                        My Performance
                    </button>

                    {canViewTeam && (
                        <button
                            onClick={() => setActiveTab('teamPerformance')}
                            className={`${
                                activeTab === 'teamPerformance' 
                                    ? 'border-[#20BEC6] text-[#20BEC6]' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                        >
                            Team Reviews
                        </button>
                    )}

                    {canViewSetup && (
                        <button
                            onClick={() => setActiveTab('setup')}
                            className={`${
                                activeTab === 'setup' 
                                    ? 'border-[#20BEC6] text-[#20BEC6]' 
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            } py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                        >
                            Performance Setup
                        </button>
                    )}
                </nav>
            </div>

            {/* Content Area */}
            <div className="mt-4">
                {activeTab === 'myPerformance' && (
                    <MyPerformance userProfile={userProfile} appId={appId} showModal={showModal} />
                )}
                
                {activeTab === 'teamPerformance' && canViewTeam && (
                    <TeamPerformance userProfile={userProfile} appId={appId} showModal={showModal} />
                )}

                {activeTab === 'setup' && canViewSetup && (
                    <PerformanceSetup userProfile={userProfile} appId={appId} showModal={showModal} />
                )}
            </div>
        </div>
    );
}